import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Navbar from '../components/navbar'
import Footer from '../components/footer'
import ScrollToTop from '../components/scroll-to-top'
import useCsrfToken from '../hooks/useCsrfToken'

import hero from '../assets/images/hero/puertointerior.jpg'
import maternity from '../assets/images/paquetes/1.jpg'
import surgery from '../assets/images/servicios/1.jpg'
import laboratory from '../assets/images/servicios/laboratorio.jpg'
import icon1 from '../assets/images/branding/icon2.png'
import icon2 from '../assets/images/branding/icon7.png'

const getPuertoBranchId = () => {
    const configuredId = process.env.BRANCH_PUERTO_ID || process.env.REACT_APP_BRANCH_PUERTO_ID || '1';
    const branchId = Number(configuredId);

    return Number.isFinite(branchId) ? branchId : 1;
}

const stripTags = (html = '') => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
}

const DELIVERY_PRICE_FALLBACKS = {
    naturalDelivery1Night: '18,000',
    csectionDelivery1Night: '25,000',
    csectionDelivery2Nights: '29,000',
    retainerDelivery: '1,000',
}

const formatDeliveryPrice = (value, fallback) => {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    const amount = Number(value);

    if (!Number.isFinite(amount)) {
        return fallback;
    }

    return new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

const benefits = [
    {
        title: 'Más cerca de ti',
        text: 'Ubicación estratégica para Puerto Interior, Silao y la zona industrial del Bajío.',
    },
    {
        title: 'Atención ágil',
        text: 'Consulta, estudios diagnósticos, hospitalización y procedimientos en una misma ubicación.',
    },
    {
        title: 'Atención integral',
        text: 'Servicios médicos para acompañarte en distintas etapas de tu salud.',
    },
    {
        title: 'Respaldo y experiencia',
        text: 'Más de 85 años de experiencia respaldan la atención de Hospital Aranda de la Parra.',
    },
]

export default function SucursalPuertoInterior() {
    const { csrfToken } = useCsrfToken();
    const [services, setServices] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(true);
    const [servicesError, setServicesError] = useState(false);
    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(true);
    const [packagesError, setPackagesError] = useState(false);
    const [specialists, setSpecialists] = useState([]);
    const [specialistsLoading, setSpecialistsLoading] = useState(true);
    const [specialistsError, setSpecialistsError] = useState(false);
    const [deliveryPrices, setDeliveryPrices] = useState(DELIVERY_PRICE_FALLBACKS);

    const API_URL = process.env.REACT_APP_API_URL;
    const puertoBranchId = getPuertoBranchId();
    const basedomain = process.env.REACT_APP_BASE_URL;
    const pageUrl = basedomain ? `${basedomain}/sucursales/puerto-interior` : '/sucursales/puerto-interior';
    const metadataImage = basedomain ? `${basedomain}${hero}` : hero;
    const metadataTitle = 'Hospital Aranda de La Parra Puerto Interior | Atención médica en Silao';
    const metadataDescription = 'Consulta médica, urgencias, maternidad, cirugía, hospitalización, hemodiálisis, laboratorio e imagenología en Puerto Interior.';

    const hospitalSchema = {
        '@context': 'https://schema.org',
        '@type': 'Hospital',
        name: 'Hospital Aranda de la Parra Puerto Interior',
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Blvd. Mineral de Cata #901, esquina Blvd. Mineral de Rayas',
            addressLocality: 'Silao',
            addressRegion: 'Guanajuato',
            addressCountry: 'MX',
        },
        telephone: '+52 472 500 0000',
        url: pageUrl,
    };

    useEffect(() => {
        const transformData = (data) => {
            return data
                .map(item => {
                    const description = stripTags(item.description);

                    return {
                        id: item.id,
                        image: item.primary_image ? `${API_URL}${item.primary_image}` : hero,
                        tag: item.listingtag ?? 'Servicios',
                        name: item.title,
                        slug: item.slug,
                        desc: description.length > 115 ? `${description.substring(0, 115)}...` : description,
                    };
                });
        };

        if (csrfToken != null) {
            fetch(`${API_URL}/api/branchlistings/instalation/1/0/${puertoBranchId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            }).then(response => response.json())
                .then(data => {
                    const listings = Array.isArray(data) ? data : data?.data ?? [];
                    setServices(transformData(listings));
                    setServicesError(false);
                })
                .catch(error => {
                    console.error('Error fetching Puerto Interior services:', error);
                    setServicesError(true);
                })
                .finally(() => setServicesLoading(false));
        }
    }, [API_URL, csrfToken, puertoBranchId]);

    useEffect(() => {
        const formatPrice = (price) => {
            return price ? price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : '0';
        };

        const transformData = (data) => {
            return data
                .map(item => {
                    const description = stripTags(item.description);

                    return {
                        id: item.id,
                        image: item.primary_image ? `${API_URL}${item.primary_image}` : maternity,
                        tag: 'Paquetes',
                        name: item.title,
                        slug: item.slug,
                        desc: description.length > 90 ? `${description.substring(0, 90)}...` : description,
                        package_type: item.package_type,
                        price: formatPrice(item.price),
                        price_discount: formatPrice(item.price_discount),
                        hasdiscount: item.price_discount > 0,
                    };
                });
        };

        if (csrfToken != null) {
            fetch(`${API_URL}/api/branchlistings/package/1/0/${puertoBranchId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            }).then(response => response.json())
                .then(data => {
                    const listings = Array.isArray(data) ? data : data?.data ?? [];
                    setPackages(transformData(listings));
                    setPackagesError(false);
                })
                .catch(error => {
                    console.error('Error fetching Puerto Interior packages:', error);
                    setPackagesError(true);
                })
                .finally(() => setPackagesLoading(false));
        }
    }, [API_URL, csrfToken, puertoBranchId]);

    useEffect(() => {
        const transformData = (data) => {
            return data
                .map(item => ({
                    id: item.id,
                    image: item.primary_image ? `${API_URL}${item.primary_image}` : hero,
                    name: item.title,
                    specialty: item.specialty_title,
                    slug: item.slug,
                }));
        };

        if (csrfToken != null) {
            fetch(`${API_URL}/api/branchlistings/directory/1/0/${puertoBranchId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            }).then(response => response.json())
                .then(data => {
                    const listings = Array.isArray(data) ? data : data?.data ?? [];
                    setSpecialists(transformData(listings));
                    setSpecialistsError(false);
                })
                .catch(error => {
                    console.error('Error fetching Puerto Interior specialists:', error);
                    setSpecialistsError(true);
                })
                .finally(() => setSpecialistsLoading(false));
        }
    }, [API_URL, csrfToken, puertoBranchId]);

    useEffect(() => {
        if (csrfToken != null) {
            fetch(`${API_URL}/api/settings/delivery-prices`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`Delivery prices request failed with status ${response.status}`);
                }

                return response.json();
            }).then(data => {
                const prices = data?.data ?? data ?? {};

                setDeliveryPrices({
                    naturalDelivery1Night: formatDeliveryPrice(prices.price_natural_delivery_1_night, DELIVERY_PRICE_FALLBACKS.naturalDelivery1Night),
                    csectionDelivery1Night: formatDeliveryPrice(prices.price_csection_delivery_1_night, DELIVERY_PRICE_FALLBACKS.csectionDelivery1Night),
                    csectionDelivery2Nights: formatDeliveryPrice(prices.price_csection_delivery_2_nights, DELIVERY_PRICE_FALLBACKS.csectionDelivery2Nights),
                    retainerDelivery: formatDeliveryPrice(prices.price_retainer_delivery, DELIVERY_PRICE_FALLBACKS.retainerDelivery),
                });
            }).catch(error => {
                console.error('Error fetching Puerto Interior delivery prices:', error);
                setDeliveryPrices(DELIVERY_PRICE_FALLBACKS);
            });
        }
    }, [API_URL, csrfToken]);

    return (
        <>
            <Helmet>
                <title>{metadataTitle}</title>
                <meta name="description" content={metadataDescription} />
                <meta property="og:title" content={metadataTitle} />
                <meta property="og:description" content={metadataDescription} />
                <meta property="og:image" content={metadataImage} />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metadataTitle} />
                <meta name="twitter:description" content={metadataDescription} />
                <meta name="twitter:image" content={metadataImage} />
                <meta itemProp="name" content={metadataTitle} />
                <meta itemProp="description" content={metadataDescription} />
                <meta itemProp="image" content={metadataImage} />
                <script type="application/ld+json">{JSON.stringify(hospitalSchema)}</script>
            </Helmet>

            <Navbar />

            <main className="sucursal-page">
                <section className="sucursal-hero d-table w-100" style={{ backgroundImage: `url(${hero})` }}>
                    <div className="bg-overlay bg-gradient-overlay"></div>
                    <div className="container position-relative">
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <div className="title-heading">
                                    <span className="badge rounded-pill bg-success text-light mb-3">Hospital Aranda de la Parra · Puerto Interior</span>
                                    <h1 className="heading fw-semibold mb-3 text-white title-dark">Cuidamos de ti, ahora más cerca.</h1>
                                    <p className="para-desc text-white-50 mb-4">
                                        Atención médica, estudios diagnósticos y servicios hospitalarios para acompañarte en distintas etapas de tu salud.
                                    </p>
                                    <div className="d-flex flex-wrap gap-2">
                                        <a href="#contacto" className="btn btn-success">Solicitar información</a>
                                        <a href="#servicios" className="btn btn-light">Ver servicios</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="sucursal-quick">
                    <div className="container">
                        <div className="row g-0 shadow bg-white rounded overflow-hidden">
                            <div className="col-lg-3 col-md-6">
                                <div className="quick-item">
                                    <i className="mdi mdi-hospital-building text-success"></i>
                                    <h5>Atención médica cuando la necesitas</h5>
                                    <p>Consulta, estudios diagnósticos y atención hospitalaria en una misma ubicación.</p>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                                <div className="quick-item">
                                    <i className="mdi mdi-microscope text-secondary"></i>
                                    <h5>Especialistas y tecnología</h5>
                                    <p>Laboratorio e imagenología para apoyar diagnósticos oportunos y precisos.</p>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                                <div className="quick-item">
                                    <i className="mdi mdi-bed text-primary"></i>
                                    <h5>Capacidad hospitalaria</h5>
                                    <p>Cirugía, maternidad, hospitalización y servicios para distintas necesidades de salud.</p>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                                <div className="quick-item border-end-0">
                                    <i className="mdi mdi-shield-check-outline text-success"></i>
                                    <h5>Respaldo y experiencia</h5>
                                    <p>Más de 85 años de experiencia respaldan la atención que brindamos.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="section" id="servicios">
                    <div className="container">
                        <div className="row justify-content-center mb-5">
                            <div className="col-lg-8">
                                <div className="section-title text-center">
                                    <img src={icon1} className="img-fluid title-icon mb-4" alt="" />
                                    <h2 className="title mb-3">Servicios disponibles en Puerto Interior</h2>
                                    <p className="text-muted para-desc mx-auto mb-0">
                                        Desde atención médica de primer contacto hasta procedimientos hospitalarios, contamos con servicios diseñados para acompañarte en distintas etapas de tu salud.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            {services.length > 0 ? (
                                services.map((service) => (
                                    <div className="col-lg-4 col-md-6 col-12 mt-4 pt-2" key={service.id}>
                                        <div className="listing position-relative overflow-hidden shadow rounded p-4 d-flex flex-column h-100">
                                            <div className="position-relative">
                                                <div className="listing-image position-relative overflow-hidden rounded shadow object-fit-cover">
                                                    <Link to={`/servicio/${service.slug}`}>
                                                        <img src={service.image} className="img-fluid" alt={service.name} />
                                                    </Link>
                                                </div>

                                                <div className="position-absolute top-100 start-0 translate-middle-y ms-2">
                                                    <span className="badge bg-success">{service.tag}</span>
                                                </div>
                                            </div>

                                            <div className="pt-4 flex-grow-1 d-flex flex-column">
                                                <Link to={`/servicio/${service.slug}`} className="text-dark title h5">{service.name}</Link>
                                                <p className="text-muted my-2 flex-grow-1">{service.desc}</p>
                                                <Link to={`/servicio/${service.slug}`} className="btn btn-success w-100 btn-sm text-light mt-auto">
                                                    Conocer más <i className="mdi mdi-chevron-right align-middle"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 mt-4 pt-2 text-center">
                                    {servicesLoading ? (
                                        <p className="text-muted">Cargando servicios...</p>
                                    ) : servicesError ? (
                                        <p className="text-muted">No fue posible cargar los servicios de Puerto Interior en este momento.</p>
                                    ) : (
                                        <p className="text-muted">No hay servicios publicados para Puerto Interior por el momento.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="section bg-inverted" id="maternidad">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-5">
                                <div className="position-relative overflow-hidden rounded shadow">
                                    <img src={maternity} className="img-fluid sucursal-feature-image" alt="Maternidad Puerto Interior" />
                                </div>
                            </div>
                            <div className="col-lg-7 mt-4 mt-lg-0">
                                <div className="ms-lg-4">
                                    <span className="badge rounded-pill bg-soft-success text-success mb-3">Maternidad Puerto Interior</span>
                                    <h2 className="title mb-3">La llegada de tu bebé está cada vez más cerca. Y nosotros también.</h2>
                                    <p className="text-muted">
                                        Acompañamos esta etapa con atención especializada para mamá y bebé, instalaciones confortables y paquetes con costo garantizado en Puerto Interior.
                                    </p>

                                    <div className="row mt-4">
                                        <div className="col-md-4 mt-3">
                                            <div className="price-box">
                                                <small>Parto natural</small>
                                                <strong>${deliveryPrices.naturalDelivery1Night}</strong>
                                                <span>1 noche</span>
                                            </div>
                                        </div>
                                        <div className="col-md-4 mt-3">
                                            <div className="price-box">
                                                <small>Cesárea</small>
                                                <strong>${deliveryPrices.csectionDelivery1Night}</strong>
                                                <span>1 noche</span>
                                            </div>
                                        </div>
                                        <div className="col-md-4 mt-3">
                                            <div className="price-box">
                                                <small>Cesárea</small>
                                                <strong>${deliveryPrices.csectionDelivery2Nights}</strong>
                                                <span>2 noches</span>
                                            </div>
                                        </div>
                                    </div>

                                    <h5 className="mt-4">Tu paquete incluye:</h5>
                                    <ul className="sucursal-checklist text-muted">
                                        <li>Material médico y medicamentos.</li>
                                        <li>Atención en cuneros.</li>
                                        <li>Tipo de sangre, Grupo y RH.</li>
                                        <li>Tamiz Neonatal de 61 elementos.</li>
                                        <li>Alojamiento conjunto y lactancia materna.</li>
                                        <li>Médicos neonatólogos certificados.</li>
                                        <li>Pañalera de regalo y sesión fotográfica para tu bebé.</li>
                                    </ul>

                                    <p className="mb-3"><strong>Aparta tu paquete desde ${deliveryPrices.retainerDelivery} MXN</strong> al menos 15 días antes de tu fecha probable de parto y asegura tu precio.</p>
                                    <Link to="/paquetes/maternidad" className="btn btn-success">Ver paquetes de maternidad</Link>
                                    <p className="small text-muted mt-3 mb-0">No incluye honorarios médicos, complicaciones, terapia intensiva, terapia intermedia, UCIN ni servicios no especificados dentro del paquete.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="section" id="hemodialisis">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6 order-2 order-lg-1 mt-4 mt-lg-0">
                                <span className="badge rounded-pill bg-soft-secondary text-secondary mb-3">Hemodiálisis Puerto Interior</span>
                                <h2 className="title mb-3">Atención especializada en tu tratamiento</h2>
                                <p className="text-muted">
                                    Un espacio diseñado para brindar seguridad, comodidad y acompañamiento durante cada proceso. La unidad ofrece seguimiento médico y el respaldo de una infraestructura hospitalaria preparada para dar continuidad a la atención del paciente.
                                </p>
                                <ul className="sucursal-checklist text-muted">
                                    <li>9 estaciones de hemodiálisis.</li>
                                    <li>Área de estabilización.</li>
                                    <li>Consultorio médico.</li>
                                    <li>Integración con servicios hospitalarios.</li>
                                </ul>
                                <a href="#contacto" className="btn btn-success">Solicitar información</a>
                            </div>
                            <div className="col-lg-6 order-1 order-lg-2">
                                <div className="sucursal-image-collage">
                                    <img src={laboratory} className="img-fluid rounded shadow" alt="Laboratorio Puerto Interior" />
                                    <img src={surgery} className="img-fluid rounded shadow" alt="Atención hospitalaria Puerto Interior" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="section bg-inverted" id="promociones">
                    <div className="container">
                        <div className="row align-items-end mb-4">
                            <div className="col-lg-8">
                                <div className="section-title">
                                    <h2 className="title mb-3">Beneficios y promociones para ti</h2>
                                    <p className="text-muted para-desc mb-0">
                                        Conoce opciones especiales para Puerto Interior, maternidad, hemodiálisis, check up, laboratorio e imagenología.
                                    </p>
                                </div>
                            </div>
                            {/* <div className="col-lg-4 mt-3 mt-lg-0 text-lg-end">
                                <Link to="/paquetes" className="btn btn-outline-success">Ver paquetes y promociones</Link>
                            </div> */}
                        </div>

                        <div className="row">
                            {packages.length > 0 ? (
                                packages.map((item) => (
                                    <div className="col-lg-4 col-md-6 col-12 mt-4 pt-2" key={item.id}>
                                        <div className="paquetes position-relative overflow-hidden shadow rounded p-4 d-flex flex-column h-100">
                                            <div className="position-relative">
                                                <div className="paquetes-image position-relative overflow-hidden rounded shadow object-fit-cover">
                                                    <Link to={`/paquete/${item.slug}`}>
                                                        <img src={item.image} className="img-fluid notransition" alt={item.name} />
                                                    </Link>
                                                </div>

                                                <div className="position-absolute top-100 start-0 translate-middle-y ms-2">
                                                    <span className="badge text-dark bg-light">{item.tag}</span>
                                                </div>
                                            </div>

                                            <div className="pt-4 flex-grow-1 d-flex flex-column">
                                                <Link to={`/paquete/${item.slug}`} className="text-dark title h5">{item.name}</Link>
                                                <p className="text-muted my-2 flex-grow-1">{item.desc}</p>

                                                {item.package_type === 'pricebased' && (
                                                    item.hasdiscount ? (
                                                        <div className="text-center mb-2">
                                                            <span className="text-decoration-line-through">${item.price}</span>
                                                            <br />
                                                            <span className="text-success">${item.price_discount}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center mb-2">${item.price}</div>
                                                    )
                                                )}

                                                <Link to={`/paquete/${item.slug}`} className="btn btn-success w-100 btn-sm text-light mt-auto">
                                                    {item.package_type === 'pricebased' ? 'Comprar' : 'Más información'} <i className="mdi mdi-chevron-right align-middle"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 mt-4 pt-2 text-center">
                                    {packagesLoading ? (
                                        <p className="text-muted">Cargando paquetes...</p>
                                    ) : packagesError ? (
                                        <p className="text-muted">No fue posible cargar los paquetes de Puerto Interior en este momento.</p>
                                    ) : (
                                        <p className="text-muted">No hay paquetes publicados para Puerto Interior por el momento.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="section" id="especialistas">
                    <div className="container">
                        <div className="row justify-content-center mb-4">
                            <div className="col-lg-8">
                                <div className="section-title text-center">
                                    <img src={icon2} className="img-fluid title-icon mb-4" alt="" />
                                    <h2 className="title mb-3">Atención médica especializada</h2>
                                    <p className="text-muted para-desc mx-auto mb-0">
                                        Encuentra acompañamiento médico para tus necesidades de salud en Puerto Interior.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            {specialists.length > 0 ? (
                                specialists.map((item) => (
                                    <div className="col-lg-3 col-md-6 col-12 mt-4 pt-2" key={item.id}>
                                        <div className="directory position-relative overflow-hidden rounded p-2">
                                            <div className="position-relative">
                                                <div className="card team team-primary bg-transparent text-center mb-5">
                                                    <div className="directory-image card-img team-image d-inline-block mx-auto rounded overflow-hidden">
                                                        <Link to={`/directorio/${item.slug}`} className="text-dark mb-0 title">
                                                            <img src={item.image} className="img-fluid" alt={item.name} />
                                                        </Link>
                                                    </div>

                                                    <div className="content bg-white mx-4 p-2 z-1 rounded shadow">
                                                        <Link to={`/directorio/${item.slug}`} className="text-dark mb-0 title">
                                                            {item.name}
                                                        </Link>
                                                        <br />
                                                        <small className="text-muted mb-0 mt-1 fw-normal">
                                                            {item.specialty}
                                                        </small>
                                                        <Link to={`/directorio/${item.slug}`} className="btn btn-sm p-1 btn-dark mt-2 w-100">
                                                            Ver perfil
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 mt-4 pt-2 text-center">
                                    {specialistsLoading ? (
                                        <p className="text-muted">Cargando especialistas...</p>
                                    ) : specialistsError ? (
                                        <p className="text-muted">No fue posible cargar los especialistas de Puerto Interior en este momento.</p>
                                    ) : (
                                        <p className="text-muted">No hay especialistas publicados para Puerto Interior por el momento.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="section bg-inverted">
                    <div className="container">
                        <div className="row justify-content-center mb-4">
                            <div className="col-lg-8">
                                <div className="section-title text-center">
                                    <h2 className="title mb-3">Por qué elegir Hospital Aranda de la Parra Puerto Interior</h2>
                                    <p className="text-muted para-desc mx-auto mb-0">
                                        Una unidad diseñada para acercarte servicios médicos, atención especializada y soluciones para distintas necesidades de salud.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            {benefits.map((benefit, index) => (
                                <div className="col-lg-3 col-md-6 mt-4" key={index}>
                                    <div className="sucursal-benefit h-100">
                                        <h5>{benefit.title}</h5>
                                        <p className="text-muted mb-0">{benefit.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="section" id="contacto">
                    <div className="container">
                        <div className="row align-items-stretch shadow rounded overflow-hidden bg-white">
                            <div className="col-lg-5">
                                <div className="contact-panel h-100">
                                    <span className="badge rounded-pill bg-soft-success text-success mb-3">Contacto Puerto Interior</span>
                                    <h2 className="title mb-3">Agenda o solicita información</h2>
                                    <p className="text-muted">Nuestro equipo puede ayudarte a resolver dudas, orientarte sobre servicios y acompañarte en tu proceso de atención.</p>

                                    <div className="contact-line">
                                        <i className="mdi mdi-map-marker-outline"></i>
                                        <p><strong>Dirección</strong><br />Blvd. Mineral de Cata #901, esquina Blvd. Mineral de Rayas.<br />Polígono de Servicios, Guanajuato Puerto Interior.<br />Silao, Guanajuato.</p>
                                    </div>
                                    <div className="contact-line">
                                        <i className="mdi mdi-phone-outline"></i>
                                        <p><strong>Teléfono</strong><br /><a href="tel:4725000000" className="text-dark">472 500 0000</a></p>
                                    </div>
                                    <div className="contact-line">
                                        <i className="mdi mdi-ambulance"></i>
                                        <p><strong>Urgencias</strong><br />472 500 0000 ext. 8600</p>
                                    </div>

                                    <a href="tel:4725000000" className="btn btn-success mt-2">Llamar ahora</a>
                                </div>
                            </div>
                            <div className="col-lg-7 p-0">
                                <iframe
                                    className="sucursal-map"
                                    src="https://www.google.com/maps?q=@21.0045284,-101.5097879&output=embed"
                                    style={{ border: '0' }}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Hospital Aranda de la Parra Puerto Interior"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
            <ScrollToTop />
        </>
    )
}
